(() => {
  const config = window.CoinPurchaseLimit;
  const maximum = Math.floor(Number(config?.maximum));
  if (!(maximum > 0)) return;
  const originalFetch = window.fetch.bind(window);
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const message = () => config.message.replaceAll('[limit]', maximum);
  const limitError = state => Object.assign(new Error(message()), { purchaseLimit: true, cartCount: state.item_count });
  const cartSummary = count => count == null ? '' : 'You can purchase up to ' + maximum + ' items per day.\nYour cart currently contains ' + count + (count === 1 ? ' item.' : ' items.');
  let queue = Promise.resolve();
  const run = task => {
    const next = queue.then(() => navigator.locks ? navigator.locks.request('coin-cart-quantity', task) : task());
    queue = next.catch(() => {});
    return next;
  };
  const cart = async () => {
    const response = await originalFetch(root + 'cart.js', { cache: 'no-store' });
    if (!response.ok) throw Error('Unable to check your cart. Please try again.');
    return response.json();
  };
  const number = value => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0) throw Error('Please enter a valid quantity.');
    return n;
  };
  const decode = async (input, init) => {
    let body = init?.body;
    if (body == null && input instanceof Request) {
      const copy = input.clone();
      body = (copy.headers.get('content-type') || '').includes('json') ? await copy.json() : await copy.formData();
    }
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch { body = new URLSearchParams(body); }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const data = Object.fromEntries(body.entries());
      const items = [];
      const updates = {};
      for (const [key, value] of body.entries()) {
        const item = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
        if (item) { items[item[1]] ||= {}; items[item[1]][item[2]] = value; }
        const update = key.match(/^updates\[([^\]]+)\]$/);
        if (update) updates[update[1]] = value;
      }
      if (items.length) data.items = items.filter(Boolean);
      if (Object.keys(updates).length) data.updates = updates;
      if (body.has('updates[]')) data.updates = body.getAll('updates[]');
      return data;
    }
    return body || {};
  };
  const projected = (state, operation, data) => {
    if (operation === 'add') return state.item_count + (data.items || [data]).reduce((sum, item) => sum + number(item.quantity ?? 1), 0);
    if (operation === 'clear') return 0;
    const quantities = state.items.map(item => item.quantity);
    if (operation === 'change') {
      const index = data.line ? number(data.line) - 1 : state.items.findIndex(item => item.key === String(data.id) || String(item.variant_id) === String(data.id));
      if (index < 0 || index >= quantities.length) throw Error('Your cart changed. Please refresh and try again.');
      quantities[index] = number(data.quantity);
    }
    if (operation === 'update' && data.updates) {
      for (const [key, value] of Object.entries(data.updates)) {
        const index = Array.isArray(data.updates) ? Number(key) : state.items.findIndex(item => item.key === key || String(item.variant_id) === key);
        if (index >= 0 && index < quantities.length) quantities[index] = number(value);
        else if (number(value) > 0) quantities.push(number(value));
      }
    }
    return quantities.reduce((sum, n) => sum + n, 0);
  };
  window.fetch = (input, init) => {
    const url = new URL(input instanceof Request ? input.url : input, location.href);
    const operation = url.origin === location.origin && url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/)?.[1];
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (!operation || method !== 'POST') return originalFetch(input, init);
    return run(async () => {
      try {
        const data = await decode(input, init);
        const state = await cart();
        const total = projected(state, operation, data);
        // Let an already-over-limit cart decrease so the customer can recover.
        if (total > maximum && !(operation !== 'add' && total < state.item_count)) throw limitError(state);
        return await originalFetch(input, init);
      } catch (error) {
        const purchaseLimit = error.purchaseLimit === true;
        if (purchaseLimit) showError(document.activeElement, error.message, error.cartCount);
        return new Response(JSON.stringify({ status: 422, purchaseLimit, message: error.message, description: error.message, errors: error.message }), { status: 422, headers: { 'Content-Type': 'application/json' } });
      }
    });
  };
  const highlightQuantities = (element, text) => {
    element.replaceChildren();
    String(text).split(/(\d+)/).forEach(part => {
      if (/^\d+$/.test(part)) {
        const number = document.createElement('strong');
        number.className = 'coin-purchase-limit-quantity';
        number.textContent = part;
        element.append(number);
      } else element.append(document.createTextNode(part));
    });
  };
  const showError = (target, text, count) => {
    let node = document.getElementById('coin-purchase-limit-error');
    if (node) {
      highlightQuantities(node.querySelector('p'), text);
      highlightQuantities(node.querySelector('small'), cartSummary(count));
      node.querySelector('small').hidden = count == null;

      return;
    }
    node = document.createElement('dialog');
    node.id = 'coin-purchase-limit-error';
    node.setAttribute('aria-labelledby', 'coin-purchase-limit-message');
    const content = document.createElement('p');
    content.id = 'coin-purchase-limit-message';
    highlightQuantities(content, text);
    const note = document.createElement('small');
    note.id = 'coin-purchase-limit-note';
    highlightQuantities(note, cartSummary(count));
    note.hidden = count == null;
    node.setAttribute('aria-describedby', note.id);
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = 'OK';
    close.autofocus = true;
    close.addEventListener('click', () => node.close());
    node.append(content, note, close);
    node.addEventListener('click', event => {
      const rect = node.getBoundingClientRect();
      if (event.target === node && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) node.close();
    });
    node.addEventListener('close', () => {

      node.remove();
      if (target?.isConnected && typeof target.focus === 'function') target.focus({ preventScroll: true });
    });
    document.body.append(node);
    node.showModal();

  };
  const approved = new WeakSet();
  let checking = false;
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (approved.has(form)) { approved.delete(form); return; }
    const url = new URL(form.action, location.href);
    const add = /\/cart\/add(?:\.js)?$/.test(url.pathname);
    const checkout = event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(url.pathname);
    if (!add && !checkout) return;
    // Product-form requests go through the serialized fetch check above.
    if (add && form.closest('product-form')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checking) return;
    checking = true;
    const target = event.submitter || form;
    try {
      await run(async () => {
        const state = await cart();
        const data = await decode(null, { body: new FormData(form) });
        const total = projected(state, add ? 'add' : 'update', data);
        if (total > maximum || (checkout && state.item_count > maximum)) throw limitError(state);
      });
      approved.add(form);
      form.requestSubmit(event.submitter || undefined);
    } catch (error) { showError(target, error.message, error.cartCount); }
    finally { checking = false; }
  }, true);
  document.addEventListener('click', async event => {
    const link = event.target.closest('a[href]');
    if (!link || !/\/checkout\/?$/.test(new URL(link.href).pathname)) return;
    event.preventDefault();
    if (checking) return;
    checking = true;
    try {
      await run(async () => { const state = await cart(); if (state.item_count > maximum) throw limitError(state); });
      location.assign(link.href);
    } catch (error) { showError(link, error.message, error.cartCount); }
    finally { checking = false; }
  }, true);
})();
